import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ListingCard from '../components/ListingCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Package, Store, Briefcase, Calendar, FolderKanban, Building2,
  Pause, Play, Infinity as InfinityIcon, AlertCircle, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const CAT_ICONS = { shop: Store, service: Briefcase, event: Calendar, project: FolderKanban, business: Building2 };

function InventoryControls({ listing, onChange, language }) {
  const [busy, setBusy] = useState(false);
  const [restock, setRestock] = useState('');
  const [error, setError] = useState('');
  const qty = listing.available_quantity;
  const isPaused = !!listing.is_paused;
  const isOOS = qty === 0;

  const call = async (body) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.patch(`${API}/listings/${listing.id}/inventory`, body);
      onChange(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const togglePause = () => call({ is_paused: !isPaused });
  const setUnlimited = () => call({ set_unlimited: true });
  const submitRestock = (e) => {
    e.preventDefault();
    const n = parseInt(restock, 10);
    if (!n || n <= 0) return;
    setRestock('');
    call({ restock_amount: n });
  };

  return (
    <div
      className="mt-2 bg-white border border-[#E5E4E0] rounded-xl p-3 text-xs space-y-2"
      data-testid={`inventory-controls-${listing.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-medium text-[#121212]">
          <Package className="w-3.5 h-3.5 text-[#5C5C5C]" />
          {qty === null || qty === undefined ? (
            <span className="flex items-center gap-1">
              <InfinityIcon className="w-3.5 h-3.5" />
              {language === 'lv' ? 'Bezgalīgs' : 'Unlimited'}
            </span>
          ) : (
            <span data-testid={`qty-badge-${listing.id}`}>
              {language === 'lv' ? 'Krājums' : 'Stock'}: <b>{qty}</b>
            </span>
          )}
        </div>
        {isPaused && (
          <span className="flex items-center gap-1 text-[#B45309] bg-[#FFF7ED] border border-[#FED7AA] px-2 py-0.5 rounded-full">
            <Pause className="w-3 h-3" />
            {language === 'lv' ? 'Pauzēts' : 'Paused'}
          </span>
        )}
        {!isPaused && isOOS && (
          <span className="flex items-center gap-1 text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] px-2 py-0.5 rounded-full">
            <AlertCircle className="w-3 h-3" />
            {language === 'lv' ? 'Izpārdots' : 'Sold out'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={togglePause}
          disabled={busy}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736] transition-colors disabled:opacity-50"
          data-testid={`toggle-pause-${listing.id}`}
        >
          {isPaused ? (
            <><Play className="w-3 h-3" /> {language === 'lv' ? 'Atsākt' : 'Resume'}</>
          ) : (
            <><Pause className="w-3 h-3" /> {language === 'lv' ? 'Pauzēt' : 'Pause'}</>
          )}
        </button>

        {qty !== null && qty !== undefined && (
          <button
            type="button"
            onClick={setUnlimited}
            disabled={busy}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#E5E4E0] hover:border-[#D97736] hover:text-[#D97736] transition-colors disabled:opacity-50"
            data-testid={`set-unlimited-${listing.id}`}
          >
            <InfinityIcon className="w-3 h-3" /> {language === 'lv' ? 'Padarīt bezgalīgu' : 'Set unlimited'}
          </button>
        )}

        <form onSubmit={submitRestock} className="flex items-center gap-1 ml-auto">
          <Input
            type="number"
            min="1"
            step="1"
            value={restock}
            onChange={(e) => setRestock(e.target.value)}
            placeholder={language === 'lv' ? 'Pievienot' : 'Add'}
            className="h-7 w-20 text-xs"
            data-testid={`restock-input-${listing.id}`}
          />
          <Button
            type="submit"
            disabled={busy || !restock}
            size="sm"
            className="h-7 px-2 bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-xs"
            data-testid={`restock-submit-${listing.id}`}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {language === 'lv' ? 'Papildināt' : 'Restock'}
          </Button>
        </form>
      </div>
      {error && <div className="text-[#B91C1C]" data-testid={`inv-error-${listing.id}`}>{error}</div>}
    </div>
  );
}

export default function MyListingsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/my-listings`);
        setListings(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user, navigate]);

  const updateOne = (updated) => {
    setListings((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  };

  const categoryTabs = ['all', 'shop', 'service', 'event', 'project', 'business'];
  const categoryLabels = {
    all: language === 'lv' ? 'Visi' : 'All',
    shop: language === 'lv' ? 'Veikals' : 'Shop',
    service: language === 'lv' ? 'Pakalpojumi' : 'Services',
    event: language === 'lv' ? 'Pasākumi' : 'Events',
    project: language === 'lv' ? 'Projekti' : 'Projects',
    business: language === 'lv' ? 'Uzņēmumi' : 'Businesses',
  };

  const filtered = activeTab === 'all' ? listings : listings.filter(l => l.category === activeTab);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="my-listings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-[#121212]">{t('myListings')}</h1>
            <p className="text-[#5C5C5C] mt-1">{listings.length} {language === 'lv' ? 'sludinājumi' : 'listings'}</p>
          </div>
          <Link to="/create-listing">
            <Button className="bg-[#D97736] hover:bg-[#C06528] text-white" data-testid="create-listing-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('createListing')}
            </Button>
          </Link>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categoryTabs.map((tab) => {
            const count = tab === 'all' ? listings.length : listings.filter(l => l.category === tab).length;
            if (tab !== 'all' && count === 0) return null;
            const Icon = CAT_ICONS[tab];
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab
                    ? 'bg-[#D97736] text-white shadow-md'
                    : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#D97736] hover:text-[#D97736]'
                }`}
                data-testid={`my-tab-${tab}`}
              >
                {Icon && <Icon className="w-4 h-4" />}
                {categoryLabels[tab]} ({count})
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={`skel-${i}`} className="bg-white rounded-xl h-72 animate-pulse" />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((listing) => (
              <div key={listing.id} className="flex flex-col">
                <ListingCard listing={listing} />
                {(listing.category === 'shop') && (
                  <InventoryControls
                    listing={listing}
                    language={language}
                    onChange={updateOne}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-[#F3F2EE] rounded-full flex items-center justify-center">
              <Package className="w-12 h-12 text-[#5C5C5C]" />
            </div>
            <h3 className="font-outfit text-xl font-semibold text-[#121212] mb-2">
              {activeTab === 'all'
                ? (language === 'lv' ? 'Nav sludinājumu' : 'No listings yet')
                : (language === 'lv' ? `Nav sludinājumu kategorijā "${categoryLabels[activeTab]}"` : `No listings in "${categoryLabels[activeTab]}"`)}
            </h3>
            <Link to="/create-listing">
              <Button className="mt-4 bg-[#D97736] hover:bg-[#C06528] text-white">
                <Plus className="w-4 h-4 mr-2" /> {t('createListing')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
